import { useEmbedMode } from "../layouts/EmbedLayout";
import { isPartnerSsoSession } from "../utils/embedFlow";

// Single source of truth for "are we in embed mode?".
//
// Returns `true` if EITHER:
//   - The component is rendered inside the EmbedLayout subtree
//     (`useEmbedMode().isEmbed === true`), OR
//   - The current session was bootstrapped via partner-SSO
//     (in-memory JWT carries `flow: "partner_sso"`).
//
// The second branch is defence-in-depth: if a stray `<Link to="/">`
// pulls the user OUT of `/embed/*` to a tenant route, we still want
// route guards / logout flows to behave as if the session is embed
// mode. The token lives in memory until the EmbedTokenProvider
// unmounts; a brief hop through `/` keeps the JWT around so the
// claim check is still meaningful.
//
// Use this hook (NOT `useEmbedMode` directly) in any code that needs
// to know "is this user a partner-SSO user?" — route guards, logout
// handlers, signup-link gates. Use `useEmbedMode` only for "is this
// component inside the embed route tree?" (suppressing layout chrome,
// rewriting navigate() targets via useTenantNavigate, etc.).
const useIsEmbedMode = () => {
  const { isEmbed } = useEmbedMode();
  // Pure read of in-memory token — no React state involvement, so no
  // re-render dependency. The hook is intentionally not reactive on
  // the JWT side because the token is set ONCE per iframe lifecycle
  // (mount via ?token=, no in-place refresh) and never goes back to
  // null without the component unmounting.
  return isEmbed || isPartnerSsoSession();
};

export default useIsEmbedMode;
